import React from 'react';
import { QRCodeSVG } from 'qrcode.react';

import CopyButton from '../../../components/copyButton';
import CustomButton from '../../../components/customButton';
import CustomDropdown from '../../../components/customDropdown';
import CustomInput from '../../../components/customInput';
import InfinityLoader from '../../../components/dropdownSearch/infinityLoader';
import Tags from '../../../components/tags';
import { isConnectionActive, isValidDestinationUrl } from '../webEmbedUtils';

const OptionTags = ({ items, onRemove, disabled = false }) => {
  if (!items || items.length === 0) return <div className="empty-selection">No items selected</div>;

  return (
    <div className="selection-tags">
      {items.map(item => (
        <Tags
          key={item.value}
          value={item.label}
          hasCloseButton={!disabled}
          onClose={disabled ? undefined : () => onRemove(item.value)}
        />
      ))}
    </div>
  );
};

export const WebEmbedInfoForm = ({ webEmbed, onChange, showDestinationUrl = false, disabled = false }) => (
  <div className="web-embed-form">
    <div className="form-group">
      <label>Name</label>
      <CustomInput
        isEditable={!disabled}
        type="text"
        value={webEmbed.name}
        placeholder="Enter a name for the web embed"
        onChange={event => onChange({ ...webEmbed, name: event.target.value })}
        errorMessage={!webEmbed.name.trim() ? 'This value is required' : ''}
      />
    </div>
    {showDestinationUrl && (
      <div className="form-group">
        <label>Destination URL</label>
        <CustomInput
          isEditable={!disabled}
          type="url"
          value={webEmbed.destinationUrl}
          placeholder="example.com, https://example.com or app://destination"
          onChange={event => onChange({ ...webEmbed, destinationUrl: event.target.value })}
          errorMessage={
            !webEmbed.destinationUrl.trim()
              ? 'This value is required'
              : !isValidDestinationUrl(webEmbed.destinationUrl)
                ? 'Enter a valid URL'
                : ''
          }
        />
      </div>
    )}
  </div>
);

export const WebEmbedTargetingForm = ({
  webEmbed,
  onChange,
  attributionWindows,
  campaignOptions,
  campaignTotalSize,
  onLoadCampaigns,
  isLoading,
  error,
  onRetry,
  disabled = false
}) => {
  const removeCampaign = campaignId =>
    onChange({ ...webEmbed, campaigns: webEmbed.campaigns.filter(campaign => campaign.value !== campaignId) });

  const addCampaign = campaignId => {
    if (webEmbed.campaigns.some(campaign => String(campaign.value) === String(campaignId))) return;

    const campaign = campaignOptions.find(option => String(option.value) === String(campaignId));
    if (campaign) onChange({ ...webEmbed, campaigns: [...webEmbed.campaigns, campaign] });
  };

  const campaignTags = webEmbed.campaigns.map(campaign => {
    const campaignOption = campaignOptions.find(
      option => String(option.value) === String(campaign.value)
    );

    return {
      ...campaign,
      label: campaignOption?.label || campaign.displayName || campaign.label || campaign.value
    };
  });

  return (
    <div className="web-embed-form">
      {error && (
        <div className="web-embed-error">
          <span>{error}</span>
          <CustomButton classes="btn btn-secondary" title="Retry" handleClick={onRetry} />
        </div>
      )}
      <div className="form-group web-embed-campaigns">
        <InfinityLoader
          key={`campaigns-${webEmbed.campaigns.map(campaign => campaign.value).join('-')}`}
          label="Campaigns"
          options={campaignOptions}
          totalSize={campaignTotalSize}
          onLoading={onLoadCampaigns}
          onSelect={addCampaign}
          disabled={disabled || !!error}
          leftAlign
          customWidth="100%"
          outline
        />
        <OptionTags items={campaignTags} onRemove={removeCampaign} disabled={disabled} />
      </div>
      <div className="form-group web-embed-window">
        <CustomDropdown
          label="Attribution Window"
          items={attributionWindows}
          valueField="id"
          displayField="label"
          selectedItem={webEmbed.attributionWindow?.id}
          disabled={disabled || isLoading || attributionWindows.length === 0}
          handleSelection={(event, index) => onChange({ ...webEmbed, attributionWindow: attributionWindows[index] })}
        />
      </div>
    </div>
  );
};

export const QrCodePreview = ({ webEmbed, error }) => {
  const qrTrackingUrl = webEmbed.qrTrackingUrl?.trim();

  if (!webEmbed.id) {
    return (
      <div className="qr-step activation-ready">
        <div className="qr-placeholder" aria-hidden="true">
          <i className="uil uil-qrcode-scan" />
        </div>
        <div className="qr-copy">
          <h3>Ready to activate</h3>
          <span>Activation persists this Web Embed and retrieves its destination and QR tracking URL from the API.</span>
          {error && <div className="web-embed-error mt-4">{error}</div>}
        </div>
      </div>
    );
  }

  return (
    <div className="qr-step">
      {qrTrackingUrl ? (
        <div className="qr-code">
          <QRCodeSVG value={webEmbed.qrTrackingUrl} size={142} title={`QR code for ${webEmbed.displayName}`} />
        </div>
      ) : (
        <div className="qr-unavailable">QR tracking URL unavailable</div>
      )}
      <div className="qr-copy">
        <h3>QR tracking URL</h3>
        {qrTrackingUrl ? (
          <a href={webEmbed.qrTrackingUrl} target="_blank" rel="noopener noreferrer">
            {webEmbed.qrTrackingUrl}
          </a>
        ) : (
          <p>The Web Property API did not return a QR tracking URL.</p>
        )}
        {webEmbed.destinationUrl && <span>Destination: {webEmbed.destinationUrl}</span>}
        <span>The Web Embed is persisted as Pending Data until the API reports that tracking data was received.</span>
      </div>
    </div>
  );
};

export const TrackingSnippetPanel = ({ webEmbed }) => {
  const isActive = isConnectionActive(webEmbed);
  const snippetId = `web-embed-snippet-${webEmbed.id}`;

  return (
    <div className="tracking-snippet-panel">
      <div className={`connection-state ${isActive ? 'active' : 'pending'}`}>
        <h3>{isActive ? 'Connection active' : 'Connection pending'}</h3>
        <p>
          {isActive
            ? 'Tracking data has been received for this Web Embed.'
            : 'Waiting for tracking data. Install the snippet below and refresh if the state does not update.'}
        </p>
      </div>
      {webEmbed.embedSnippet ? (
        <div className="tracking-snippet-code">
          <pre id={snippetId}>{webEmbed.embedSnippet}</pre>
          <CopyButton id={snippetId} tooltip="Copy embed code" />
        </div>
      ) : (
        <div className="snippet-unavailable">The Web Property API did not return an embed snippet.</div>
      )}
      {!isActive && (
        <a
          href={
            webEmbed.troubleshootingUrl ||
            `mailto:support@admobilize.com?subject=${encodeURIComponent(`Web Embed connection: ${webEmbed.name}`)}`
          }
          className="troubleshooting-link"
          target={webEmbed.troubleshootingUrl ? '_blank' : undefined}
          rel={webEmbed.troubleshootingUrl ? 'noopener noreferrer' : undefined}
        >
          Troubleshoot the connection
        </a>
      )}
    </div>
  );
};
